import { useEffect, useState } from "react"
import { useGetIngestionHistoryByIdQuery } from "~/ui-rtk/api/ingestionApi"
import { Loader } from "~/ui-rtk/components/ui/common"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import dayjs from "dayjs"
import {
  STATUS_COLORS,
  STATUS_LABELS,
  formatFileSize,
  getNormalizationSummary,
  isTerminalStatus,
} from "../utils"

type IngestionHistoryDetailProps = {
  historyId: string
}

const DELIVERY_METHOD_LABELS: Record<string, string> = {
  manual: "Manual Upload",
  email: "Email",
  hybrid: "Hybrid",
  upload: "Manual Upload",
}

export default function IngestionHistoryDetail({
  historyId,
}: IngestionHistoryDetailProps) {
  // Poll while the record is still being processed; stop once it reaches a
  // terminal state. Keep polling while a record is loading.
  const [isPolling, setIsPolling] = useState(true)
  const {
    data: history,
    isLoading,
    isError,
    error,
  } = useGetIngestionHistoryByIdQuery(historyId, {
    pollingInterval: isPolling ? 4000 : 0,
  })

  // Only stop on a terminal status for the *current* historyId. The
  // `history.id === historyId` guard ignores a stale cache hit from a
  // previously-viewed record, so switching records always resumes polling.
  useEffect(() => {
    const currentIsTerminal =
      !!history && history.id === historyId && isTerminalStatus(history.status)
    setIsPolling(!currentIsTerminal)
  }, [historyId, history?.id, history?.status])

  const normalization = history?.metadata?.normalization
  const normalizationSummary = normalization
    ? getNormalizationSummary(normalization)
    : null

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size={48} />
      </div>
    )
  }

  if (isError || !history) {
    return (
      <div className="p-4 bg-system-error-500/10 border border-system-error-500/30 rounded-lg">
        <p className="text-system-error-400">
          Could not load file details.{" "}
          {error && "data" in error ? "Please try again later." : ""}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between pr-8">
        <div>
          <h2 className="text-xl font-bold text-white">{history.fileName}</h2>
          <p className="text-sm text-platinum mt-1">
            Uploaded{" "}
            {dayjs(history.createdAt).format("MMMM D, YYYY [at] h:mm A")}
          </p>
        </div>
        <span
          className={cn(
            "inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium",
            STATUS_COLORS[history.status] || STATUS_COLORS.PENDING,
          )}
        >
          {STATUS_LABELS[history.status] || history.status}
        </span>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-basic-dark-blue/50 border border-basic-blue rounded-lg">
          <p className="text-xs text-platinum mb-1">File Size</p>
          <p className="text-white font-medium">
            {formatFileSize(history.fileSize)}
          </p>
        </div>

        <div className="p-3 bg-basic-dark-blue/50 border border-basic-blue rounded-lg">
          <p className="text-xs text-platinum mb-1">Delivery Method</p>
          <p className="text-white font-medium">
            {DELIVERY_METHOD_LABELS[history.deliveryMethod] ||
              history.deliveryMethod}
          </p>
        </div>

        {history.reportType && (
          <div className="p-3 bg-basic-dark-blue/50 border border-basic-blue rounded-lg">
            <p className="text-xs text-platinum mb-1">Report Type</p>
            <p className="text-white font-medium font-mono">
              {history.reportType}
            </p>
          </div>
        )}

        {history.processedAt && (
          <div className="p-3 bg-basic-dark-blue/50 border border-basic-blue rounded-lg">
            <p className="text-xs text-platinum mb-1">Processed</p>
            <p className="text-white font-medium">
              {dayjs(history.processedAt).format("MMM D, YYYY h:mm A")}
            </p>
          </div>
        )}
      </div>

      {/* Still processing hint */}
      {!isTerminalStatus(history.status) && (
        <div className="flex items-center gap-2 p-3 bg-basic-dark-blue/50 border border-basic-blue rounded-lg">
          <Loader size={16} />
          <p className="text-sm text-platinum">
            Processing your file — this updates automatically.
          </p>
        </div>
      )}

      {/* What we auto-fixed */}
      {normalizationSummary && normalizationSummary.fixes.length > 0 && (
        <div className="p-4 bg-system-success-500/10 border border-system-success-500/30 rounded-lg">
          <h3 className="text-base font-semibold text-system-success-400 mb-3">
            Adjusted automatically
          </h3>
          <ul className="space-y-1.5">
            {normalizationSummary.fixes.map(fix => (
              <li key={fix} className="text-sm text-platinum">
                • {fix}
              </li>
            ))}
          </ul>
          {typeof normalization?.row_count === "number" && (
            <p className="text-xs text-platinum mt-3">
              {normalization.row_count.toLocaleString()} row
              {normalization.row_count === 1 ? "" : "s"} ingested.
            </p>
          )}
        </div>
      )}

      {/* Normalization warnings (columns missing / mostly empty) */}
      {normalizationSummary && normalizationSummary.warnings.length > 0 && (
        <div className="p-4 bg-system-warning-500/10 border border-system-warning-500/30 rounded-lg">
          <h3 className="text-base font-semibold text-system-warning-400 mb-3">
            Needs attention
          </h3>
          <ul className="space-y-1.5">
            {normalizationSummary.warnings.map(warning => (
              <li key={warning} className="text-sm text-system-warning-300">
                • {warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Validation Errors */}
      {history.validationErrors && history.validationErrors.length > 0 && (
        <div className="p-4 bg-system-error-500/10 border border-system-error-500/30 rounded-lg">
          <h3 className="text-base font-semibold text-system-error-400 mb-3">
            Validation Issues ({history.validationErrors.length})
          </h3>
          <ul className="space-y-2 max-h-48 overflow-y-auto">
            {history.validationErrors.map((err, index) => (
              <li key={index} className="text-sm text-system-error-300">
                <span className="font-medium">{err.message}</span>
                {err.column && (
                  <span className="text-platinum ml-2">
                    in column &quot;{err.column}&quot;
                  </span>
                )}
                {err.row !== undefined && (
                  <span className="text-platinum ml-1">(row {err.row})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Processing Errors */}
      {history.processingErrors && history.processingErrors.length > 0 && (
        <div className="p-4 bg-system-error-500/10 border border-system-error-500/30 rounded-lg">
          <h3 className="text-base font-semibold text-system-error-400 mb-3">
            Processing Issues ({history.processingErrors.length})
          </h3>
          <ul className="space-y-2 max-h-48 overflow-y-auto">
            {history.processingErrors.map((err, index) => (
              <li key={index} className="text-sm text-system-error-300">
                <span className="font-medium">{err.message}</span>
                {err.details && (
                  <p className="text-platinum mt-1 ml-4 text-xs">
                    {err.details}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
